// Meeting timer (feature catalogue E4): one per board, shared by everyone on it and not part of undo
// (lanewerk-core board/timer.rs). Top left on the board: a clock button; once a timer runs, the
// time (counting down, red with "+" when over; counting up without a limit), pause/continue, reset
// and remove. Times are wall-clock ms of whoever pressed the button.
import type { Store } from "./store";

export interface Timer { duration: number; elapsed: number; started: number | null }

const PRESETS = [5, 10, 15, 30, 45, 60];

const $ = <T extends HTMLElement>(sel: string) => document.querySelector(sel) as T;
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

/** 75 → "1:15", 3725 → "1:02:05". */
export function clock(seconds: number): string {
  const s = Math.floor(Math.abs(seconds));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  const p = (n: number) => String(n).padStart(2, "0");
  return h ? `${h}:${p(m)}:${p(r)}` : `${m}:${p(r)}`;
}

/** Seconds elapsed at `now` (never negative when the starter's clock is ahead). */
export function elapsedAt(t: Timer, now: number): number {
  return t.elapsed + (t.started !== null ? Math.max(0, (now - t.started) / 1000) : 0);
}

export class TimerWidget {
  private box = $("#lw-timer");
  private open = $<HTMLButtonElement>("#lw-timer-open");
  private time = $("#lw-timer-time");
  private play = $<HTMLButtonElement>("#lw-timer-play");
  private reset = $<HTMLButtonElement>("#lw-timer-reset");
  private remove = $<HTMLButtonElement>("#lw-timer-remove");
  private pop = $("#lw-timer-pop");
  private t: Timer | null = null;

  constructor(private store: Store, private now: () => number = () => Date.now()) {
    store.subscribe((source) => { if (source !== "view") this.render(); });
    setInterval(() => { if (this.t?.started != null) this.renderTime(); }, 250);
    this.open.addEventListener("click", () => (this.pop.hidden ? this.showPop() : this.closePop()));
    this.time.addEventListener("click", () => { if (!this.store.readOnly) this.showPop(); });
    this.play.addEventListener("click", () => {
      const t = this.t;
      if (!t) return;
      const now = this.now();
      this.store.setTimer(t.started !== null ? { ...t, elapsed: elapsedAt(t, now), started: null } : { ...t, started: now });
    });
    this.reset.addEventListener("click", () => { if (this.t) this.store.setTimer({ ...this.t, elapsed: 0, started: null }); });
    this.remove.addEventListener("click", () => this.store.setTimer(null));
    document.addEventListener("pointerdown", (e) => {
      if (!this.pop.hidden && !this.pop.contains(e.target as Node) && !this.box.contains(e.target as Node)) this.closePop();
    });
    this.pop.addEventListener("keydown", (e) => { e.stopPropagation(); if (e.key === "Escape") this.closePop(); });
    this.render();
  }

  private start(minutes: number) {
    this.store.setTimer({ duration: Math.round(minutes * 60), elapsed: 0, started: this.now() });
    this.closePop();
  }

  private showPop() {
    const p = this.pop;
    p.replaceChildren();
    p.appendChild(el("div", "menu-title", "Meeting-Timer – für alle auf diesem Board"));
    const row = el("div", "menu-row");
    for (const m of PRESETS) {
      const b = el("button", "chip plain", `${m} Min`);
      b.type = "button";
      b.addEventListener("click", () => this.start(m));
      row.appendChild(b);
    }
    const up = el("button", "chip plain", "Ohne Limit");
    up.type = "button";
    up.title = "Zählt die Zeit hoch";
    up.addEventListener("click", () => this.start(0));
    row.appendChild(up);
    p.appendChild(row);
    const own = el("form", "timer-own");
    const input = el("input", "field");
    input.id = "lw-timer-minutes";
    input.type = "number"; input.min = "1"; input.max = "1440"; input.inputMode = "numeric";
    input.placeholder = "Minuten";
    input.setAttribute("aria-label", "Eigene Dauer in Minuten");
    const go = el("button", "primary", "Starten");
    go.type = "submit";
    own.append(input, go);
    own.addEventListener("submit", (e) => {
      e.preventDefault();
      const m = Number(input.value);
      if (Number.isFinite(m) && m >= 1 && m <= 1440) this.start(m);
      else input.setAttribute("aria-invalid", "true");
    });
    p.appendChild(own);
    p.appendChild(el("p", "menu-hint", "Rückgängig hält den Timer nicht an."));
    p.hidden = false;
    this.open.setAttribute("aria-expanded", "true");
    p.querySelector<HTMLButtonElement>("button")?.focus();
  }

  private closePop() {
    this.pop.hidden = true;
    this.open.setAttribute("aria-expanded", "false");
  }

  render() {
    const t = this.store.timer();
    this.t = t;
    const ro = this.store.readOnly;
    // readers see a running timer but cannot start, stop or change one
    this.box.hidden = ro && !t;
    this.open.hidden = ro;
    for (const b of [this.play, this.reset, this.remove]) { b.hidden = !t; b.disabled = ro; }
    this.time.hidden = !t;
    if (ro) this.closePop();
    if (!t) { this.box.dataset.state = "off"; return; }
    const running = t.started !== null;
    this.box.dataset.state = running ? "running" : "paused";
    this.play.textContent = running ? "❚❚" : "▶";
    this.play.title = running ? "Anhalten" : "Weiterlaufen lassen";
    this.play.setAttribute("aria-label", this.play.title);
    this.renderTime();
  }

  private renderTime() {
    const t = this.t;
    if (!t) return;
    const e = elapsedAt(t, this.now());
    const left = t.duration - e;
    const over = t.duration > 0 && left < 0;
    this.time.textContent = t.duration === 0 ? clock(e) : over ? `+${clock(-left)}` : clock(Math.ceil(left));
    this.box.classList.toggle("over", over);
    this.time.title = t.duration ? `Geplant: ${clock(t.duration)}` : "Ohne Limit";
  }
}
